import { useState, useEffect, useCallback, useMemo } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import useLicenseAccess from '@/hooks/useLicenseAccess';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Plus, Users, Trash2, Pencil, UsersRound, Lock, Clock, AlertTriangle, X, Bug } from 'lucide-react';
import VendorScheduleModal from '@/components/VendorScheduleModal';
import { isCarDealerUser, isDeveloperUser, isEducationUser } from '@/lib/companyVertical';
import DebugAssignmentsPanel, { buildAssignmentsDebugJson } from '@/components/DebugAssignmentsPanel';

const LICENSE_LABELS = {
  independent: 'Independiente',
  agency: 'Agencia',
  developer: 'Desarrollador',
  franchise: 'Franquiciatario',
  owner: 'Propietario',
  car_dealer: 'Car-Dealer',
};

const DAYS = [
  { key: 'monday', label: 'Lunes', short: 'Lun', isWeekday: true },
  { key: 'tuesday', label: 'Martes', short: 'Mar', isWeekday: true },
  { key: 'wednesday', label: 'Miércoles', short: 'Mié', isWeekday: true },
  { key: 'thursday', label: 'Jueves', short: 'Jue', isWeekday: true },
  { key: 'friday', label: 'Viernes', short: 'Vie', isWeekday: true },
  { key: 'saturday', label: 'Sábado', short: 'Sáb', isWeekday: false },
  { key: 'sunday', label: 'Domingo', short: 'Dom', isWeekday: false },
];

const EMPTY_VERTICAL_FILTERS = {
  property_ids: [],
  zones: [],
  car_model_ids: [],
  development_ids: [],
  includes_used: false,
  includes_promotions: false,
};

const CAR_INVENTORY_OPTIONS = [
  { value: 'new', label: 'Autos nuevos' },
  { value: 'used', label: 'Seminuevos' },
  { value: 'parts', label: 'Refacciones' },
  { value: 'service', label: 'Servicios' },
];

// ── Helpers de detección de vertical (movidos a lib/companyVertical.js en E-R3c) ──
// Se importan al inicio para que SettingsPage, OpportunitiesPage y este archivo
// compartan una sola fuente de verdad.

// Specialty default reutilizable — fuente única para nuevos grupos y resets.
const EMPTY_SPECIALTY = {
  car_inventory_types: [],
  product_ids: [],
  development_ids: [],
  brand_ids: [],
};

// Endpoint base del módulo (centralizado para no repetir magic strings).
const GROUPS_API = '/roulette/groups';

// Construye members[] a partir de un grupo del backend, con fallback legacy.
const buildMembersFromGroup = (group) => {
  if (Array.isArray(group?.members) && group.members.length > 0) {
    return group.members.map(m => ({
      vendor_id: m.vendor_id || '',
      schedule: m.schedule && typeof m.schedule === 'object' ? m.schedule : {},
      vertical_filters: { ...EMPTY_VERTICAL_FILTERS, ...(m.vertical_filters || {}) },
    }));
  }
  const legacyVendorIds = group?.vendor_ids || [];
  const legacySchedule = group?.schedule || {};
  return legacyVendorIds.map(vid => ({
    vendor_id: vid,
    schedule: (legacySchedule[vid] && typeof legacySchedule[vid] === 'object') ? legacySchedule[vid] : {},
    vertical_filters: { ...EMPTY_VERTICAL_FILTERS },
  }));
};

// Días lun-vie sin cobertura. Retorna [] si nadie ha configurado schedule todavía.
const getWeekdayCoverageGaps = (members) => {
  const hasAnyScheduledDay = (members || []).some(m =>
    m.schedule && Object.values(m.schedule).some(d => d && typeof d === 'object'),
  );
  if (!hasAnyScheduledDay) return [];
  return DAYS.filter(d => d.isWeekday).filter(d =>
    !(members || []).some(m => m.schedule?.[d.key]?.active),
  );
};

export default function GroupsPage() {
  const { user } = useAuth();
  const { licenseType, isIndependent } = useLicenseAccess();

  // Detección de vertical mediante helpers reutilizables.
  const isCarDealer = isCarDealerUser(user);
  const isDeveloper = isDeveloperUser(user);
  // E-R6d: Education se trata como "tenant que opera por productos".
  // No requiere checkboxes propios — el guard `products.length > 0` ya
  // muestra la sección Productos. Lo exponemos por si flujos futuros
  // (filtros UI, copy, badges) necesitan diferenciar Colegios.
  const isEducation = isEducationUser(user);
  // eslint-disable-next-line no-unused-expressions
  void isEducation;

  const [groups, setGroups] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    name: '',
    is_active: true,
    max_leads_per_vendor: -1,
    members: [],
    specialty: { ...EMPTY_SPECIALTY },
  });
  const [vendorToAdd, setVendorToAdd] = useState('');
  const [scheduleEditingFor, setScheduleEditingFor] = useState(null);
  const [products, setProducts] = useState([]);
  const [developments, setDevelopments] = useState([]);
  const [brands, setBrands] = useState([]);

  // ── Debug panel para diagnóstico de asignaciones ─────────────────────
  const [debugOpen, setDebugOpen] = useState(false);
  const [debugJson, setDebugJson] = useState(null);
  const [debugLoading, setDebugLoading] = useState(false);

  const openDebug = async () => {
    setDebugLoading(true);
    try {
      const [rrRes, leadsRes] = await Promise.all([
        api.get('/leads/round-robin-config').catch(() => ({ data: {} })),
        api.get('/leads').catch(() => ({ data: [] })),
      ]);
      const rrConfig = rrRes.data || {};
      // paginacion-lista-leads: GET /leads ahora devuelve {items,total,skip,limit}.
      const recentLeads = Array.isArray(leadsRes.data) ? leadsRes.data : (leadsRes.data?.items || leadsRes.data?.leads || []);
      setDebugJson(buildAssignmentsDebugJson({
        groups, users: vendors, user, rrConfig, recentLeads,
        extras: { products_count: products.length, developments_count: developments.length },
      }));
      setDebugOpen(true);
    } finally {
      setDebugLoading(false);
    }
  };

  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';

  const loadGroups = useCallback(() => {
    Promise.all([
      api.get(GROUPS_API),
      api.get('/team').catch(() => ({ data: [] })),
      api.get('/products?active_only=true').catch(() => ({ data: [] })),
      api.get('/developments?active_only=true').catch(() => ({ data: [] })),
      api.get('/brands').catch(() => ({ data: [] })),
    ]).then(([groupsData, teamData, productsData, devsData, brandsData]) => {
      setGroups(Array.isArray(groupsData.data) ? groupsData.data : []);
      const team = Array.isArray(teamData.data) ? teamData.data : [];
      setVendors(team.filter(m =>
        m.is_active !== false
        && m.role !== 'superadmin',
      ));
      const productsArr = Array.isArray(productsData.data)
        ? productsData.data
        : (productsData.data?.products || []);
      setProducts(Array.isArray(productsArr) ? productsArr : []);
      const devsArr = Array.isArray(devsData.data)
        ? devsData.data
        : (devsData.data?.developments || []);
      setDevelopments(Array.isArray(devsArr) ? devsArr : []);
      const brandsArr = Array.isArray(brandsData.data)
        ? brandsData.data
        : (brandsData.data?.brands || []);
      setBrands(Array.isArray(brandsArr) ? brandsArr : []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  useEffect(() => { loadGroups(); }, [loadGroups]);

  const openNew = () => {
    setEditing(null);
    setForm({
      name: '',
      is_active: true,
      max_leads_per_vendor: -1,
      members: [],
      specialty: { ...EMPTY_SPECIALTY },
    });
    setVendorToAdd('');
    setDialogOpen(true);
  };

  const openEdit = (group) => {
    setEditing(group.id);
    setForm({
      name: group.name || '',
      is_active: group.is_active !== false,
      max_leads_per_vendor: group.max_leads_per_vendor ?? -1,
      members: buildMembersFromGroup(group),
      // Merge defensivo: EMPTY_SPECIALTY garantiza que todos los arrays existen
      // aunque el grupo viejo en MongoDB no tenga development_ids.
      specialty: {
        ...EMPTY_SPECIALTY,
        ...(group.specialty || {}),
      },
    });
    setVendorToAdd('');
    setDialogOpen(true);
  };

  const saveGroup = async () => {
    if (!form.name.trim()) { toast.error('El nombre es obligatorio'); return; }
    if (!form.members.length) { toast.error('Agrega al menos un vendedor'); return; }
    try {
      const payload = {
        name: form.name.trim(),
        is_active: form.is_active,
        max_leads_per_vendor: parseInt(form.max_leads_per_vendor) || -1,
        members: form.members,
        // vendor_ids: compatibilidad con motor de asignación legacy.
        vendor_ids: form.members.map(m => m.vendor_id).filter(Boolean),
        // specialty: si la empresa no es Car Dealer forzamos car_inventory_types=[]
        // para no dejar datos sucios si un admin cambió la vertical de la empresa.
        specialty: {
          ...(form.specialty || {}),
          car_inventory_types: isCarDealer ? (form.specialty?.car_inventory_types || []) : [],
          product_ids: form.specialty?.product_ids || [],
          development_ids: isDeveloper ? (form.specialty?.development_ids || []) : [],
          brand_ids: brands.length ? (form.specialty?.brand_ids || []) : [],
        },
      };
      if (editing) {
        await api.put(`${GROUPS_API}/${editing}`, payload);
        toast.success('Grupo actualizado');
      } else {
        await api.post(GROUPS_API, payload);
        toast.success('Grupo creado');
      }
      setDialogOpen(false);
      loadGroups();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al guardar');
    }
  };

  const deleteGroup = async (id) => {
    if (!window.confirm('Eliminar grupo?')) return;
    await api.delete(`${GROUPS_API}/${id}`);
    toast.success('Eliminado');
    loadGroups();
  };

  const addMember = (vendorId) => {
    if (!vendorId) return;
    if (form.members.some(m => m.vendor_id === vendorId)) {
      toast.error('Ese vendedor ya está en el grupo');
      return;
    }
    setForm(f => ({
      ...f,
      members: [
        ...f.members,
        { vendor_id: vendorId, schedule: {}, vertical_filters: { ...EMPTY_VERTICAL_FILTERS } },
      ],
    }));
    setVendorToAdd('');
  };

  const removeMember = (vendorId) => {
    setForm(f => ({
      ...f,
      members: f.members.filter(m => m.vendor_id !== vendorId),
    }));
  };

  // Toggle de un valor (string) en un array dentro de form.specialty.
  const toggleGroupSpecialty = (field, value) => {
    setForm(f => {
      const current = Array.isArray(f.specialty?.[field]) ? f.specialty[field] : [];
      const next = current.includes(value)
        ? current.filter(x => x !== value)
        : [...current, value];
      return {
        ...f,
        specialty: { ...(f.specialty || {}), [field]: next },
      };
    });
  };

  const getVendorName = (id) => vendors.find(v => v.id === id)?.name || id;
  const getVendorEmail = (id) => vendors.find(v => v.id === id)?.email || '';
  const getVendorInitials = (id) => {
    const name = getVendorName(id) || '';
    return name.split(/\s+/).filter(Boolean).slice(0, 2).map(s => s[0]).join('').toUpperCase() || '?';
  };

  const availableVendorsToAdd = useMemo(() => (
    vendors.filter(v => !form.members.some(m => m.vendor_id === v.id))
  ), [vendors, form.members]);

  const currentMemberEditing = scheduleEditingFor
    ? form.members.find(m => m.vendor_id === scheduleEditingFor)
    : null;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (isIndependent) {
    return (
      <div data-testid="groups-page-locked">
        <div className="mb-8">
          <h2 className="font-heading text-2xl font-bold text-white">Grupos de Asignacion</h2>
          <p className="text-muted-foreground text-sm mt-1">Segmentacion de vendedores por criterio</p>
        </div>
        <Card className="bg-card/50 backdrop-blur-md border-white/5">
          <CardContent className="py-16 text-center">
            <Lock className="w-12 h-12 mx-auto text-amber-400/60 mb-4" />
            <p className="text-white font-semibold mb-2">No disponible en tu licencia</p>
            <p className="text-muted-foreground text-sm max-w-md mx-auto">
              Los grupos de asignacion son para equipos con mas de un vendedor. Actualiza tu licencia a <strong className="text-primary">Agencia</strong>, <strong className="text-violet-400">Desarrollador</strong> o <strong className="text-cyan-400">Franquicia</strong> para usar esta funcionalidad.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const groupsVisibles = groups.filter(g => g.is_active !== false);

  return (
    <TooltipProvider delayDuration={150}>
      <div data-testid="groups-page">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="font-heading text-2xl font-bold text-white">Grupos de Asignacion</h2>
            <p className="text-muted-foreground text-sm mt-1">
              {groupsVisibles.length} grupo{groupsVisibles.length !== 1 ? 's' : ''} · Licencia: <Badge variant="outline" className="ml-1 text-[10px] border-primary/30 text-primary">{LICENSE_LABELS[licenseType] || licenseType}</Badge>
            </p>
          </div>
          {isAdmin && (
            <>
            <Button
              variant="ghost"
              size="sm"
              onClick={openDebug}
              disabled={debugLoading}
              className="text-xs text-muted-foreground hover:text-white gap-1"
              title="Debug Info — para diagnóstico con Codex"
              data-testid="debug-groups-btn"
            >
              <Bug size={14} />
              {debugLoading ? 'Cargando…' : 'Debug'}
            </Button>
            <Button onClick={openNew} className="bg-primary hover:bg-primary/90 shadow-lg shadow-primary/20 font-semibold" data-testid="add-group-btn">
              <Plus className="w-4 h-4 mr-2" />Nuevo Grupo
            </Button>
            </>
          )}
        </div>

        {groupsVisibles.length === 0 ? (
          <Card className="bg-card/50 backdrop-blur-md border-white/5">
            <CardContent className="py-16 text-center">
              <UsersRound className="w-12 h-12 mx-auto text-muted-foreground/30 mb-4" />
              <p className="text-muted-foreground">Aun no hay grupos configurados.</p>
              <p className="text-xs text-muted-foreground/60 mt-2">Los grupos permiten rutear leads a los vendedores correctos automaticamente.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {groupsVisibles.map(g => {
              const groupMembers = buildMembersFromGroup(g);
              const gaps = getWeekdayCoverageGaps(groupMembers);
              return (
                <Card key={g.id} className={`bg-card/50 backdrop-blur-md border-white/5 hover:border-primary/20 ${g.is_active === false ? 'opacity-60' : ''}`} data-testid={`group-card-${g.id}`}>
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                          <Users className="w-5 h-5 text-primary" />
                        </div>
                        <div>
                          <p className="font-semibold text-white text-sm">{g.name}</p>
                          {gaps.length > 0 && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Badge variant="outline" className="text-[9px] mt-1 border-amber-400/40 text-amber-300 bg-amber-400/10 cursor-help" data-testid={`group-coverage-warning-${g.id}`}>
                                  <AlertTriangle className="w-2.5 h-2.5 mr-1" /> Días sin cobertura
                                </Badge>
                              </TooltipTrigger>
                              <TooltipContent side="bottom" className="bg-[#1F2833] border-white/10 text-xs">
                                <p className="font-medium text-amber-300 mb-1">Sin vendedor asignado:</p>
                                <ul className="text-white/80">
                                  {gaps.map(d => <li key={d.key}>· {d.label}</li>)}
                                </ul>
                              </TooltipContent>
                            </Tooltip>
                          )}
                        </div>
                      </div>
                      {isAdmin && (
                        <div className="flex items-center gap-1">
                          <Button variant="ghost" size="sm" onClick={() => openEdit(g)} className="h-7 w-7 p-0 text-muted-foreground hover:text-primary hover:bg-primary/10" data-testid={`edit-group-${g.id}`}>
                            <Pencil className="w-3.5 h-3.5" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => deleteGroup(g.id)} className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive hover:bg-destructive/10" data-testid={`delete-group-${g.id}`}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      )}
                    </div>
                    <div className="space-y-2">
                      <div>
                        <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Vendedores ({groupMembers.length})</p>
                        <div className="flex flex-wrap gap-1">
                          {groupMembers.slice(0, 6).map(m => (
                            <span key={m.vendor_id} className="text-[10px] px-2 py-0.5 rounded-full bg-white/5 border border-white/10 text-white/80" data-testid={`group-member-${g.id}-${m.vendor_id}`}>
                              {getVendorName(m.vendor_id)}
                            </span>
                          ))}
                          {groupMembers.length > 6 && (
                            <span className="text-[10px] px-2 py-0.5 rounded-full bg-white/5 text-muted-foreground">+{groupMembers.length - 6}</span>
                          )}
                        </div>
                      </div>
                      {g.max_leads_per_vendor > 0 && (
                        <p className="text-[10px] text-muted-foreground">Max. {g.max_leads_per_vendor} leads/vendedor</p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Modal principal del grupo */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[88vh] overflow-y-auto bg-[#1F2833] border-white/10" data-testid="group-modal">
            <DialogHeader>
              <DialogTitle className="font-heading text-white flex items-center gap-2">
                <Users className="w-5 h-5 text-primary" /> {editing ? 'Editar grupo' : 'Nuevo grupo'}
              </DialogTitle>
              <DialogDescription className="text-muted-foreground text-xs">
                Licencia: {LICENSE_LABELS[licenseType]} · Configura vendedores y horarios por miembro
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Nombre del grupo</Label>
                <Input data-testid="group-name-input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Equipo Polanco / Torres del Valle / etc." className="bg-black/20 border-white/10 focus:border-primary" />
              </div>

              {(isCarDealer || products.length > 0 || (isDeveloper && developments.length > 0)) && (
                <div className="space-y-2 rounded-lg border border-white/5 bg-black/10 p-3" data-testid="group-specialty-section">
                  <div>
                    <Label className="text-xs text-white">Especialidad del grupo</Label>
                    <p className="text-[10px] text-muted-foreground mt-0.5">
                      Define qué inventario o productos atiende este grupo. Sin marcar = atiende todos.
                    </p>
                  </div>
                  {isCarDealer && (
                    <div data-testid="group-specialty-car-inventory">
                      <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1.5">
                        Tipo de inventario
                      </p>
                      <div className="grid grid-cols-2 gap-1.5">
                        {CAR_INVENTORY_OPTIONS.map(opt => {
                          const checked = (form.specialty?.car_inventory_types || []).includes(opt.value);
                          return (
                            <button
                              key={opt.value}
                              type="button"
                              onClick={() => toggleGroupSpecialty('car_inventory_types', opt.value)}
                              className={`px-2 py-1.5 rounded text-left text-[11px] border transition-colors ${
                                checked
                                  ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-300'
                                  : 'bg-black/20 border-white/10 text-white/70 hover:border-white/20'
                              }`}
                              data-testid={`specialty-car-${opt.value}`}
                            >
                              {checked ? '☑' : '☐'} {opt.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  {products.length > 0 && (
                    <div>
                      <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1.5">
                        Productos ({(form.specialty?.product_ids || []).length}/{products.length})
                      </p>
                      <div className="grid grid-cols-1 gap-1 max-h-40 overflow-y-auto pr-1">
                        {products.map(p => {
                          const pid = p.id;
                          const checked = (form.specialty?.product_ids || []).includes(pid);
                          return (
                            <button
                              key={pid}
                              type="button"
                              onClick={() => toggleGroupSpecialty('product_ids', pid)}
                              className={`px-2 py-1 rounded text-left text-[11px] border transition-colors ${
                                checked
                                  ? 'bg-cyan-500/10 border-cyan-500/30 text-cyan-300'
                                  : 'bg-black/20 border-white/10 text-white/70 hover:border-white/20'
                              }`}
                              data-testid={`specialty-product-${pid}`}
                            >
                              {checked ? '☑' : '☐'} {p.name || p.title || pid}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  {brands.length > 0 && (
                    <div data-testid="group-specialty-brands">
                      <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1.5">
                        Marcas que atiende ({(form.specialty?.brand_ids || []).length}/{brands.length})
                      </p>
                      <div className="grid grid-cols-2 gap-1.5 max-h-[120px] overflow-y-auto pr-1">
                        {brands.map(b => {
                          const checked = (form.specialty?.brand_ids || []).includes(b.id);
                          return (
                            <button
                              key={b.id}
                              type="button"
                              onClick={() => toggleGroupSpecialty('brand_ids', b.id)}
                              className={`px-2 py-1.5 rounded text-left text-[11px] border transition-colors ${
                                checked
                                  ? 'bg-violet-500/10 border-violet-500/30 text-violet-300'
                                  : 'bg-black/20 border-white/10 text-white/70 hover:border-white/20'
                              }`}
                              data-testid={`specialty-brand-${b.id}`}
                            >
                              {checked ? '☑' : '☐'} {b.name || b.title || b.id}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  {isDeveloper && developments.length > 0 && (
                    <div data-testid="group-specialty-developments">
                      <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1.5">
                        Desarrollos que atiende ({(form.specialty?.development_ids || []).length}/{developments.length})
                      </p>
                      <div className="grid grid-cols-2 gap-1.5 max-h-[120px] overflow-y-auto pr-1">
                        {developments.map(dev => {
                          const checked = (form.specialty?.development_ids || []).includes(dev.id);
                          return (
                            <button
                              key={dev.id}
                              type="button"
                              onClick={() => toggleGroupSpecialty('development_ids', dev.id)}
                              className={`px-2 py-1.5 rounded text-left text-[11px] border transition-colors ${
                                checked
                                  ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-300'
                                  : 'bg-black/20 border-white/10 text-white/70 hover:border-white/20'
                              }`}
                              data-testid={`specialty-dev-${dev.id}`}
                            >
                              {checked ? '☑' : '☐'} {dev.name || dev.title || dev.id}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="space-y-2">
                <Label>Agregar vendedor al grupo</Label>
                <div className="flex items-center gap-2">
                  <Select value={vendorToAdd} onValueChange={setVendorToAdd}>
                    <SelectTrigger className="bg-black/20 border-white/10 flex-1" data-testid="group-vendor-add-select">
                      <SelectValue placeholder={availableVendorsToAdd.length ? 'Selecciona un vendedor' : 'No hay vendedores disponibles'} />
                    </SelectTrigger>
                    <SelectContent>
                      {availableVendorsToAdd.map(v => (
                        <SelectItem key={v.id} value={v.id} data-testid={`group-vendor-opt-${v.id}`}>
                          <span className="block">
                            <span className="block text-xs font-medium">{v.name}</span>
                            <span className="block text-[10px] text-muted-foreground">{v.email}</span>
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    type="button"
                    onClick={() => addMember(vendorToAdd)}
                    disabled={!vendorToAdd}
                    className="bg-primary hover:bg-primary/90 font-semibold"
                    data-testid="group-add-member-btn"
                  >
                    <Plus className="w-4 h-4 mr-1" /> Agregar
                  </Button>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Miembros del grupo ({form.members.length})</Label>
                {form.members.length === 0 ? (
                  <div className="p-4 rounded-lg border border-dashed border-white/10 bg-black/10 text-center" data-testid="group-members-empty">
                    <p className="text-xs text-muted-foreground">Aún no has agregado vendedores. Usa el selector de arriba.</p>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-72 overflow-y-auto pr-1" data-testid="group-members-list">
                    {form.members.map(m => {
                      const activeDays = DAYS.filter(d => m.schedule?.[d.key]?.active).map(d => d.short);
                      return (
                        <div key={m.vendor_id} className="flex items-center gap-3 p-2.5 rounded-lg bg-black/20 border border-white/5" data-testid={`group-member-row-${m.vendor_id}`}>
                          <div className="w-9 h-9 rounded-full bg-primary/15 border border-primary/20 flex items-center justify-center text-primary text-xs font-semibold flex-shrink-0">
                            {getVendorInitials(m.vendor_id)}
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-xs font-medium text-white truncate">{getVendorName(m.vendor_id)}</p>
                            <p className="text-[10px] text-muted-foreground truncate">
                              {activeDays.length > 0
                                ? `Activo: ${activeDays.join(', ')}`
                                : (getVendorEmail(m.vendor_id) || 'Sin horario configurado')}
                            </p>
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => setScheduleEditingFor(m.vendor_id)}
                            className="h-7 px-2 text-muted-foreground hover:text-primary hover:bg-primary/10"
                            data-testid={`group-member-edit-schedule-${m.vendor_id}`}
                          >
                            <Clock className="w-3.5 h-3.5 mr-1" />
                            <span className="text-[10px]">Horario</span>
                          </Button>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeMember(m.vendor_id)}
                            className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                            data-testid={`group-member-remove-${m.vendor_id}`}
                          >
                            <X className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Max. leads / vendedor</Label>
                  <Input
                    data-testid="group-max-leads"
                    type="number"
                    min="-1"
                    value={form.max_leads_per_vendor}
                    onChange={e => setForm({ ...form, max_leads_per_vendor: e.target.value })}
                    placeholder="-1 = sin limite"
                    className="bg-black/20 border-white/10 focus:border-primary"
                  />
                  <p className="text-[10px] text-muted-foreground">-1 = sin limite</p>
                </div>
                <div className="space-y-2">
                  <Label>Activo</Label>
                  <div className="flex items-center gap-2 p-2 rounded-lg bg-black/20 border border-white/10 h-10">
                    <Switch checked={form.is_active} onCheckedChange={v => setForm({ ...form, is_active: v })} data-testid="group-active-switch" />
                    <span className="text-xs text-white/70">{form.is_active ? 'Activo' : 'Inactivo'}</span>
                  </div>
                </div>
              </div>

              <Button onClick={saveGroup} className="w-full bg-primary hover:bg-primary/90 shadow-lg shadow-primary/20 font-semibold" data-testid="save-group-btn">
                {editing ? 'Actualizar grupo' : 'Crear grupo'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Drawer para editar horario por miembro (mismo diseño que ProductsPage) */}
        <VendorScheduleModal
          open={!!scheduleEditingFor}
          onOpenChange={(open) => !open && setScheduleEditingFor(null)}
          vendorName={currentMemberEditing ? getVendorName(currentMemberEditing.vendor_id) : ''}
          schedule={currentMemberEditing?.schedule || {}}
          onSave={async (newSchedule) => {
            if (!currentMemberEditing) {
              setScheduleEditingFor(null);
              return;
            }
            const vendorId = currentMemberEditing.vendor_id;
            // Construir members[] actualizado con el nuevo schedule del vendor
            const updatedMembers = form.members.map(m =>
              m.vendor_id === vendorId ? { ...m, schedule: newSchedule } : m,
            );
            // 1) Actualizar state local del form (refleja al reabrir el modal)
            setForm(f => ({ ...f, members: updatedMembers }));
            // 2) Si el grupo ya existe en backend, persistir inmediatamente
            if (editing) {
              try {
                await api.put(`/roulette/groups/${editing}`, {
                  members: updatedMembers,
                  vendor_ids: updatedMembers.map(m => m.vendor_id).filter(Boolean),
                });
                // 3) Actualizar también el array global de grupos para que el
                //    listado refleje los nuevos badges de cobertura.
                setGroups(gs => gs.map(g =>
                  g.id === editing ? { ...g, members: updatedMembers } : g,
                ));
                toast.success('Horarios guardados');
              } catch (e) {
                toast.error(e.response?.data?.detail || 'Error al guardar horarios');
              }
            } else {
              toast.success('Horarios actualizados');
            }
            setScheduleEditingFor(null);
          }}
        />

        {/* Debug panel para diagnóstico (Codex+MCP) */}
        <DebugAssignmentsPanel
          open={debugOpen}
          onClose={() => setDebugOpen(false)}
          debugJson={debugJson}
        />
      </div>
    </TooltipProvider>
  );
}
